import type { UserType } from "@shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { getPostAuthLanding } from "@/features/auth/utils/post-auth-redirect";
import { queryKeys } from "@/lib/query-keys";
import { useRoleAnnouncementStore } from "../store/role-announcement.store";
import { focusMainHeading } from "../utils/focus-main-heading";

/** The server's message for a refused request, or `fallback`. */
function serverMessage(error: unknown, fallback: string): string {
	const message = (error as { response?: { data?: { message?: unknown } } })
		?.response?.data?.message;
	return typeof message === "string" && message !== "" ? message : fallback;
}

interface SwitchOptions {
	/**
	 * Stay on the current page instead of going to the new role's landing
	 * page, for a prompt shown on a page the new role can use.
	 */
	stay?: boolean;
}

export interface RoleActions {
	/** The role being switched to or added, while the request is out. */
	pendingRole: UserType | null;
	/** Resolves to true once the switch is made. */
	switchTo: (role: UserType, options?: SwitchOptions) => Promise<boolean>;
	/** Resolves to true once the role is added and active. */
	add: (role: UserType) => Promise<boolean>;
}

/**
 * Switch the active role or add one. A switch goes to the new role's landing
 * page (unless `stay` is set) and is announced; an add goes to the role's
 * onboarding, or to its landing page when its profile is already finished.
 * Either then moves focus to the page's main heading. A refusal
 * shows the server's message in a toast and reloads the role list; the active
 * role stays as it was.
 */
export function useRoleActions(): RoleActions {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { switchRole, addRole } = useAuthStore();
	const announce = useRoleAnnouncementStore((state) => state.announce);
	const [pendingRole, setPendingRole] = useState<UserType | null>(null);

	const refetchRoles = useCallback((): void => {
		void queryClient.invalidateQueries({ queryKey: queryKeys.user.roles() });
	}, [queryClient]);

	const switchTo = useCallback(
		async (role: UserType, options?: SwitchOptions): Promise<boolean> => {
			setPendingRole(role);
			try {
				const user = await switchRole(role);
				refetchRoles();
				if (!options?.stay) navigate(getPostAuthLanding(user.userType));
				announce(
					t("roleSwitcher.switched", {
						role: t(`roleSession.roleNames.${role}`),
					}),
				);
				focusMainHeading({ waitForNewPage: !options?.stay });
				return true;
			} catch (error) {
				toast.error(serverMessage(error, t("roleSwitcher.switchFailed")));
				refetchRoles();
				return false;
			} finally {
				setPendingRole(null);
			}
		},
		[switchRole, refetchRoles, navigate, announce, t],
	);

	const add = useCallback(
		async (role: UserType): Promise<boolean> => {
			setPendingRole(role);
			try {
				const user = await addRole(role);
				refetchRoles();
				navigate(
					user.profileCompleted
						? getPostAuthLanding(user.userType)
						: `/onboarding/${role}`,
				);
				focusMainHeading({ waitForNewPage: true });
				return true;
			} catch (error) {
				toast.error(serverMessage(error, t("roleSwitcher.addFailed")));
				refetchRoles();
				return false;
			} finally {
				setPendingRole(null);
			}
		},
		[addRole, refetchRoles, navigate, t],
	);

	return { pendingRole, switchTo, add };
}
